<template>
  <Modal
    title="Job Opportunity"
    class-name="job-opportunity-modal"
    :saving="saving"
    @submit="handleSave"
    @hide-modal="$emit('hide-modal')"
  >
    <div>
      <Select
        v-model="formData.owner_id"
        name="Technician"
        :options="options"
        placeholder="Select Technician"
        label="Owner"
        class="mb-2"
        full-width
        data-cy="job-opportunity-owner-select-input"
      />
      <Select
        v-model="formData.opportunity_status"
        name="Opportunity Status"
        :options="statusOptions"
        placeholder="Select Status"
        :field-error="firstErrorMessage(v$.formData?.opportunity_status)"
        label="Opportunity Status"
        class="mb-2"
        full-width
      />
      <div class="d-flex gap-3">
        <ChoiceBox
          v-model="formData.lead_type"
          name="Lead Type"
          class="flex-1"
          label="Lead Type"
          type="lead_types"
          placeholder="None"
        />
        <ChoiceBox
          v-model="formData.lead_source"
          name="Lead Source"
          class="flex-1"
          label="Lead Source"
          type="lead_sources"
          placeholder="None"
        />
      </div>
      <Select
        v-if="telephonyEnabled"
        v-model="formData.booking_call_id"
        label="Booking Call"
        class="mt-2"
        :options="bookingCallOptions"
        placeholder="Select from linked calls"
        search
        full-width
      />
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getProperty } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import ChoiceBox from "@/components/ChoiceBox.vue";
import Modal from "@/components/Modal.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import { formatTime } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    Modal,
    ChoiceBox,
    SelectInput
  },
  props: {
    jobId: {
      type: Number,
      default: null
    },
    opportunityStatus: {
      type: String,
      default: ""
    },
    technician: {
      type: Object,
      default: null
    },
    leadType: {
      type: String,
      default: null
    },
    leadSource: {
      type: String,
      default: null
    },
    bookingCallId: {
      type: Number,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      open: false,
      statusOptions: [],
      saving: false,
      linkedCalls: [],
      formData: {
        owner_id: null,
        opportunity_status: this.opportunityStatus,
        lead_type: this.leadType,
        lead_source: this.leadSource,
        booking_call_id: this.bookingCallId
      }
    };
  },
  computed: {
    ...mapState({
      techniciansForJob: "techniciansForJob",
      options: ({ techniciansForJob }) =>
        techniciansForJob.map((tech) => ({
          value: tech.technician_profile_id,
          label: `${tech.first_name} ${tech.last_name}`,
          icon: tech.archived ? "fas fa-archive text-gray" : null,
          hintLabel: tech.archived ? "archived" : null
        })),
      telephonyEnabled({ tenant }) {
        return getProperty(tenant, "call_rail_enabled");
      }
    }),
    bookingCallOptions() {
      return this.linkedCalls.map((call) => {
        const { id, started_at, caller, lead } = call;
        let label = `${id} | ${formatTime(started_at)} ${caller.name} ${
          caller.phone_number
        }`;
        if (getProperty(lead, "tracking_name")) {
          label += ` (${lead.tracking_name})`;
        }
        return {
          label,
          value: id
        };
      });
    }
  },
  watch: {
    techniciansForJob(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setId();
      }
    }
  },
  mounted() {
    this.fetchTechniciansForJob({ jobId: this.jobId });
    this.fetchOpportunityStatuses({ jobId: this.jobId }).then((res) => {
      this.statusOptions = res.data;
    });
    this.fetchJobLinkedCalls({ jobId: this.jobId }).then((res) => {
      this.linkedCalls = res.data;
    });
  },
  validations() {
    return {
      formData: {
        opportunity_status: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    ...mapActions([
      "fetchTechniciansForJob",
      "setJobOpportunity",
      "fetchOpportunityStatuses",
      "fetchJobLinkedCalls"
    ]),
    firstErrorMessage,
    handleSave() {
      this.v$.$validate().then((valid) => {
        if (!valid) return;
        this.saving = true;
        this.setJobOpportunity({
          jobId: this.jobId,
          ...this.formData
        })
          .then(() => {
            window.notify.success("Opportunity updated successfully!");
            this.$emit("hide-modal");
            this.$emit("save-success");
            EventBus.$emit("refresh-datatable");
          })
          .finally(() => (this.saving = false));
      });
    },
    setId() {
      if (this.technician) {
        if (this.technician.technician_id) {
          return (this.formData.owner_id = this.technician.technician_id);
        }
        const tech = this.techniciansForJob.find(
          (tech) => tech.user_id === this.technician.id
        );
        this.formData.owner_id = tech ? tech.technician_profile_id : null;
      }
    }
  }
};
</script>

<style lang="scss">
.job-opportunity-modal {
  .modal-dialog {
    max-width: 500px;
  }
}
</style>
